import React, { useEffect, useState } from "react";
import { Trophy, X, PartyPopper, Ticket } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useI18n } from "../i18n";
import { tx } from "../lib/miniI18n";

/* Results published while the phone was offline: compared against the saved timeline snapshot and shown the moment we're back online. */
export const OfflineResultAlerts = () => {
  const navigate = useNavigate();
  const { lang } = useI18n();
  const ta = lang === "ta";
  const [missed, setMissed] = useState([]);
  const [myBets, setMyBets] = useState(null);
  useEffect(() => {
    const snapshot = () => { try { return JSON.parse(localStorage.getItem("rl_timeline_cache") || "null"); } catch { return null; } };
    const onOffline = () => { const s = snapshot(); if (s) localStorage.setItem("rl_offline_snapshot", JSON.stringify(s)); };
    const onOnline = async () => {
      let before = null;
      try { before = JSON.parse(localStorage.getItem("rl_offline_snapshot") || "null"); } catch {}
      if (!before) return;
      localStorage.removeItem("rl_offline_snapshot");
      try {
        const { data } = await api.get("/lineup/today");
        if (data.date !== before.date) return;
        const doneBefore = new Set((before.shows || []).filter((s) => s.number).map((s) => s.game_id));
        const fresh = (data.shows || []).filter((s) => s.number && !doneBefore.has(s.game_id));
        if (fresh.length) {
          setMissed(fresh);
          // Offline bet reminder: which of MY bets on those shows won / lost.
          try {
            const ids = new Set(fresh.map((s) => s.game_id));
            const { data: bets } = await api.get("/bets/my?tab=completed");
            const mine = (bets || []).filter((b) => ids.has(b.game_id) && b.draw_date === data.date);
            const won = mine.filter((b) => b.status === "won");
            if (mine.length) setMyBets({ won: won.length, lost: mine.length - won.length, amount: won.reduce((s, b) => s + (b.win_amount || 0), 0), wonGames: new Set(won.map((b) => b.game_id)) });
          } catch {}
        }
        try { localStorage.setItem("rl_timeline_cache", JSON.stringify({ ...data, cached_at: Date.now() })); } catch {}
      } catch {}
    };
    if (navigator.onLine === false) onOffline();
    window.addEventListener("offline", onOffline); window.addEventListener("online", onOnline);
    return () => { window.removeEventListener("offline", onOffline); window.removeEventListener("online", onOnline); };
  }, []);
  if (!missed.length) return null;
  return (
    <div className="fixed top-3 inset-x-3 z-[60] max-w-md mx-auto rounded-2xl bg-slate-900 text-white shadow-2xl p-3" data-testid="offline-results-banner">
      <div className="flex items-start justify-between gap-2">
        <div className="text-xs font-black flex items-center gap-1.5"><Trophy className="w-4 h-4 text-amber-300" />{ta ? `நீங்கள் offline இருந்தபோது ${missed.length} result${missed.length > 1 ? "s" : ""} வந்தது` : `${missed.length} ${tx("results came while you were offline")}`}</div>
        <button onClick={() => { setMissed([]); setMyBets(null); }} className="p-0.5" data-testid="offline-results-dismiss"><X className="w-4 h-4" /></button>
      </div>
      {myBets && (
        <div className={`mt-2 rounded-xl px-2.5 py-1.5 text-xs font-bold flex items-center gap-2 ${myBets.won ? "bg-emerald-500/20 text-emerald-200" : "bg-white/10 text-slate-200"}`} data-testid="offline-mybets-summary">
          {myBets.won ? <PartyPopper className="w-4 h-4 text-amber-300" /> : <Ticket className="w-4 h-4" />}
          <span>{ta ? "உங்கள் bets" : tx("Your bets")}: <span className="text-emerald-300">{myBets.won} {ta ? "வெற்றி" : tx("won")}</span>{myBets.amount ? ` · ₹${myBets.amount.toLocaleString("en-IN")}` : ""} · <span className="text-rose-300">{myBets.lost} {ta ? "தோல்வி" : tx("lost")}</span></span>
        </div>
      )}
      <div className="mt-2 grid grid-cols-2 gap-1.5">
        {missed.slice(0, 6).map((s) => (
          <button key={s.game_id} onClick={() => { setMissed([]); navigate("/result"); }} className={`rounded-xl px-2 py-1.5 text-left ${myBets?.wonGames?.has(s.game_id) ? "bg-emerald-500/25 ring-1 ring-emerald-400" : "bg-white/10"}`} data-testid={`offline-result-${s.game_id}`}>
            <div className="text-[10px] text-slate-300 truncate">{s.name} · {s.draw_time}{myBets?.wonGames?.has(s.game_id) ? " · 🏆" : ""}</div>
            <div className="font-mono font-black text-amber-300 text-base tracking-widest">{s.number}</div>
          </button>
        ))}
      </div>
      <button onClick={() => { setMissed([]); navigate("/my-bets"); }} className="mt-2 w-full text-[11px] font-bold rl-gold-bg text-slate-900 rounded-xl py-1.5" data-testid="offline-results-mybets">{ta ? "என் bets-ஐ பார்" : tx("Check my bets")}</button>
    </div>
  );
};
